import type DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { ComponentProps } from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import OfferDatePicker from './OfferDatePicker'
import OfferDatePickerSelectBox from './OfferDatePickerSelectBox'
import DropdownSheetBase from 'LuxKit/Dropdown/Sheet/DropdownSheetBase'
import DropdownSheetBody from 'LuxKit/Dropdown/Sheet/DropdownSheetBody'
import DropdownSheetFooter from 'LuxKit/Dropdown/Sheet/DropdownSheetFooter'
import DropdownSheetHeader from 'LuxKit/Dropdown/Sheet/DropdownSheetHeader'
import Sheet from 'components/utils/Sheet'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import Group from 'components/utils/Group'
import CalendarAvailabilityLegend from '../CalendarAvailabilityLegend'
import type { AccommodationMobileDayCellSelectHandler } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendar.types'
import moment from 'moment'
import { useAlternativeDateSelection } from 'hooks/OfferPage/useAlternativeDateSelection'
import { buildAvailabilityVariantMap, getAlternativeDateOccupancyRooms, getAlternativeDatesDuration } from 'hooks/OfferPage/alternativeDatesUtils'
import { useAssistantSurfaceDropdownBoundary } from 'tripPlanner/assistant/layout/AssistantSurfaceLayoutContext'

import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'

const DatePickerContainer = styled(Sheet)`
  width: min(${rem(800)}, calc(100dvw - ${rem(32)}));
  max-height: ${rem(492)};
`

/** Compensates for DropdownSheetHeader's internal margin-top on the extension slot. Update if DropdownSheetHeader spacing changes. */
const LegendExtensionWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${rem(-4)};
  margin-bottom: ${rem(4)};
`

interface Props extends Pick<ComponentProps<typeof DropdownSheet>, 'open' | 'boundaryRef'> {
  currentOfferId?: string
  rooms: Array<App.Occupants>
  propertyTimezone?: string
  checkInDate?: string
  checkOutDate?: string
  isAlternativeDatesFlow: boolean
  availabilityData?: Record<string, boolean>
  availabilityLoading?: boolean
  duration?: number
}

function OfferDatePickerSelect(props: Props) {
  const {
    open,
    boundaryRef,
    currentOfferId,
    rooms,
    propertyTimezone,
    checkInDate,
    checkOutDate,
    isAlternativeDatesFlow,
    availabilityData,
    availabilityLoading,
    duration,
  } = props
  const intl = useIntl()

  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  // store dates locally until we 'commit' them. This is done for performance reasons
  const [dates, setDates] = useState<{ checkIn?: string, checkOut?: string }>({
    checkIn: checkInDate,
    checkOut: checkOutDate,
  })

  useEffect(() => {
    if (isAlternativeDatesFlow) return
    setDates({ checkIn: checkInDate, checkOut: checkOutDate })
  }, [checkInDate, checkOutDate, isAlternativeDatesFlow])

  const dispatch = useOfferPageDispatch()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const onCloseCross = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
    dispatch({ type: OfferPageActions.closeFilter })
    if (isAlternativeDatesFlow) {
      dispatch({ type: OfferPageActions.setAlternativeDatesFlow, isAlternativeDatesFlow: false })
    }
    if (!(dates.checkIn && !dates.checkOut)) {
      // only update state if it's not an 'invalid' (has check in, but not check out) selection
      dispatch({ type: OfferPageActions.setDates, checkIn: dates.checkIn, checkOut: dates.checkOut })
    }
  }, [dates.checkIn, dates.checkOut, dispatch, onUserAnalyticsAction, isAlternativeDatesFlow])

  const isInvalid = !!dates.checkIn && !dates.checkOut

  const onApply = useCallback<React.MouseEventHandler<HTMLButtonElement>>(() => {
    setAttemptedSubmit(true)
    if (!isInvalid) {
      onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.Apply)
      dispatch({ type: OfferPageActions.setDates, checkIn: dates.checkIn, checkOut: dates.checkOut })
      dispatch({ type: OfferPageActions.closeFilter })
      if (isAlternativeDatesFlow) {
        dispatch({ type: OfferPageActions.setAlternativeDatesFlow, isAlternativeDatesFlow: false })
      }
    }
  }, [dates.checkIn, dates.checkOut, dispatch, isInvalid, onUserAnalyticsAction, isAlternativeDatesFlow])

  const onReset = useCallback(() => {
    setDates({ checkIn: undefined, checkOut: undefined })
  }, [])

  const triggerRef = useRef<HTMLButtonElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const dropdownBoundary = useAssistantSurfaceDropdownBoundary(boundaryRef)

  useEffect(() => {
    if (!open) {
      // Each time we close dropdown, reset that the user attempted to submit it
      // we don't want to show the error message as soon as they open it again
      setAttemptedSubmit(false)
    }
  }, [open])

  const occupancyRooms = useMemo(() => getAlternativeDateOccupancyRooms(rooms), [rooms])

  const onAltDateRangeChange = useCallback((startDate: string, endDate: string | undefined) => {
    setDates({ checkIn: startDate, checkOut: endDate })
  }, [])

  const alternativeDatesDuration = useMemo(
    () => getAlternativeDatesDuration(dates.checkIn, dates.checkOut, duration),
    [dates.checkIn, dates.checkOut, duration],
  )

  const onAlternativeDayClick = useAlternativeDateSelection({
    startDate: dates.checkIn,
    endDate: dates.checkOut,
    offerId: currentOfferId,
    rooms: occupancyRooms,
    onDateRangeChange: onAltDateRangeChange,
  })

  const onDatesChange = useCallback<AccommodationMobileDayCellSelectHandler>(day => {
    if (isAlternativeDatesFlow) {
      onAlternativeDayClick(day)
      return
    }
    if (dates.checkIn && !dates.checkOut && moment(day.date).isAfter(dates.checkIn)) {
      setDates({ checkIn: dates.checkIn, checkOut: day.date })
    } else {
      setDates({ checkIn: day.date, checkOut: undefined })
    }
  }, [dates.checkIn, dates.checkOut, isAlternativeDatesFlow, onAlternativeDayClick])

  const availabilityVariantMap = useMemo(() => {
    if (!isAlternativeDatesFlow || !availabilityData) return undefined
    return buildAvailabilityVariantMap(availabilityData)
  }, [isAlternativeDatesFlow, availabilityData])

  const showAvailabilityLoading = isAlternativeDatesFlow && !!availabilityLoading

  const selectedMonth = useMemo(() => {
    if (isAlternativeDatesFlow && checkInDate) return checkInDate
    return undefined
  }, [isAlternativeDatesFlow, checkInDate])

  const resetDatesLabel = intl.formatMessage({
    id: 'DFYMkOzUKbON5-ZGj524',
    defaultMessage: 'Reset dates',
    description: 'Button text to reset selected dates in the date selection or calendar picker',
  })

  const modalTitle = isAlternativeDatesFlow
    ? intl.formatMessage({
        id: '1luxEYK0OS5UgBbvW8re',
        defaultMessage: 'Select check-in date',
        description: 'Title for date picker dropdown in the date selection or calendar picker',
      })
    : intl.formatMessage({
        id: '1luxEYK0OS5UgBbvW8re',
        defaultMessage: 'Select check-in date',
        description: 'Title for date picker dropdown in the date selection or calendar picker',
      })

  return <div>
    <OfferDatePickerSelectBox
      ref={triggerRef}
      controlled={open}
      checkIn={dates.checkIn}
      checkOut={dates.checkOut}
    />
    <DropdownSheetBase
      size="content"
      anchorRef={triggerRef}
      triggerRef={triggerRef}
      autoHeight={dropdownBoundary.autoHeight}
      boundaryRef={dropdownBoundary.boundaryRef}
      onClose={onCloseCross}
      open={open}
      data-testid="offer-checkin-select"
      portaled={dropdownBoundary.portaled}
    >
      <DropdownSheetHeader
        title={modalTitle}
        size="M"
        alignment="start"
        onDismissClick={onCloseCross}
        extensionSlot={isAlternativeDatesFlow ? <LegendExtensionWrapper><CalendarAvailabilityLegend duration={alternativeDatesDuration} /></LegendExtensionWrapper> : undefined}
      />
      <DropdownSheetBody ref={bodyRef}>
        <DatePickerContainer padding="24 0">
          <LoadingIndicator floating visible={showAvailabilityLoading} />
          <OfferDatePicker
            scrollerRef={bodyRef}
            size="large"
            propertyTimezone={propertyTimezone}
            checkInDate={dates.checkIn}
            checkOutDate={dates.checkOut}
            onDatesChange={onDatesChange}
            availabilityVariantMap={availabilityVariantMap}
            selectedMonth={selectedMonth}
            renderFromStart={isAlternativeDatesFlow}
          />
        </DatePickerContainer>
      </DropdownSheetBody>
      <DropdownSheetFooter
        startSlot={<>
          {isInvalid && attemptedSubmit && <BodyTextBlock
            variant="medium"
            colour="urgency-dark"
            startIcon={<LineExclamationTriangleIcon />}
          >
            {intl.formatMessage({
              id: 'THrMWAdkDv_FlwjgqqYS',
              defaultMessage: 'Select a checkout date',
              description: 'Error notification displayed when checkout date is not selected',
            })}
          </BodyTextBlock>}
          {dates.checkIn && dates.checkOut && <BodyText variant="medium">
            <Group direction="horizontal" verticalAlign="center">
              <span><FormatDateTime value={dates.checkIn} format="short-day-name-day-month" /></span>
              <LineArrowRightIcon size="XS" />
              <span><FormatDateTime value={dates.checkOut} format="short-day-name-day-month" /></span>
            </Group>
          </BodyText>}
        </>}
        secondaryActionProps={dates.checkIn ? { children: resetDatesLabel, onClick: onReset, kind: 'tertiary', size: 'large' } : undefined}
        primaryActionProps={{
          'data-testid': 'OfferDatePickerSelect-apply',
          size: 'large',
          children: intl.formatMessage({
            id: 'oUhIUmilE37uAT6cmYqQ',
            defaultMessage: 'Apply',
            description: 'Button text to apply selected dates in the date selection or calendar picker',
          }),
          onClick: onApply,
        }}
      />
    </DropdownSheetBase>
  </div>
}

type MappedProps = Pick<Props, 'open' | 'currentOfferId' | 'rooms' | 'propertyTimezone' | 'checkInDate' | 'checkOutDate' | 'isAlternativeDatesFlow' | 'availabilityData' | 'availabilityLoading' | 'duration'>

export default withOfferPageAndReduxState((state, appState): MappedProps => {
  const offerId = state.paginator?.currentOfferId ?? state.offer?.id
  const availabilityEntry = offerId ? appState.calendar.availabilityDateRange[offerId] : undefined
  return {
    open: state.activeFilter === 'checkin',
    currentOfferId: offerId,
    rooms: state.rooms,
    propertyTimezone: state.property?.location.timezone ?? state.offer?.property.timezone ?? state.bedbankOffer?.property.timezone,
    checkInDate: state.checkInDate,
    checkOutDate: state.checkOutDate,
    isAlternativeDatesFlow: !!state.isAlternativeDatesFlow,
    availabilityData: availabilityEntry?.data,
    availabilityLoading: availabilityEntry?.fetching,
    duration: state.duration,
  }
}, OfferDatePickerSelect)
